import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../../../services/api";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import toastOptions from "../../../utils/toastOptions";
import { cpfMask, currencyMask, displayDateTime } from "../../../utils/masks";
import { authenticationService } from "../../../services/auth.service";
import OrderPayment from "../order-payment/OrderPayment";
import IOrderPaymentDTO from "../../../models/IOrderPaymentDTO";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import DialogConfirm from "../../../components/DialogConfirm";
import { OrderEventType } from "../../../models/enums/OrderEventType";

type RouteParams = {
  type: string;
  id: string;
};

export default function OrderEdit() {
  const params = useParams<RouteParams>();
  const [order, setOrder] = useState<any>();
  const [loading, setLoading] = useState<boolean>(true);
  const [resending, setResending] = useState<boolean>(false);
  const [isPaymentOpen, setPaymentOpen] = useState<boolean>(false);
  const [dialog, setDialog] = useState<{
    isOpen: boolean;
    action: "cancel" | "delivered" | null;
  }>({ isOpen: false, action: null });

  useEffect(() => {
    const init = async () => {
      const id = params.id ?? 0;
      if (!id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const { data } = await api.get(`orders/${id}`);

        data.item.events = [
          {
            type: "Criado",
            eventAt: data.item.createdDate,
          },
          ...(data.item.events || []),
        ];

        setOrder(data.item);
      } catch (e: any) {
        e.messages?.forEach((m: string) => toast.warning(m, toastOptions));
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [params.id, params.type]);

  const handleResendEmail = async () => {
    if (!order?.keyOrder) return;

    setResending(true);
    try {
      await api.post(`orders/${order.keyOrder}/resend-email`);
      toast.success("E-mail reenviado com sucesso!", toastOptions);
    } catch (e: any) {
      e.messages?.forEach((m: string) => toast.error(m, toastOptions));
    } finally {
      setResending(false);
    }
  };

  const handleDialogConfirmation = async (confirmationCode: string) => {
    if (dialog.action === "cancel") {
      await handleCancelOrder(confirmationCode);
    } else if (dialog.action === "delivered") {
      await handleMarkDelivered();
    }
    setDialog({ isOpen: false, action: null });
  };

  const handleCancelOrder = async (confirmationCode: string) => {
    if (!order?.id) return;

    if (!confirmationCode) {
      toast.warning("Informe a senha", toastOptions);
      return;
    }

    try {
      setLoading(true);
      const dto = { confirmationCode: confirmationCode };

      await api.post(`orders/${order.keyOrder}/cancel`, dto);
      toast.success("Pedido cancelado com sucesso!", toastOptions);
      handleNewEvent(OrderEventType.CANCELED);
    } catch (e: any) {
      e.messages?.forEach((m: string) => toast.error(m, toastOptions));
    } finally {
      setLoading(false);
    }
  };

  const handleMarkDelivered = async () => {
    if (!order?.id) return;

    try {
      setLoading(true);
      await api.post(`orders/${order.keyOrder}/delivery`);
      toast.success("Pedido marcado como entregue!", toastOptions);
      handleNewEvent(OrderEventType.DELIVERED);
    } catch (e: any) {
      e.messages?.forEach((m: string) => toast.error(m, toastOptions));
    } finally {
      setLoading(false);
    }
  };

  const handleNewEvent = (type: OrderEventType) => {
    const newEvent = {
      type: type,
      eventAt: new Date(),
      user: { name: authenticationService.currentUserValue?.name },
    };

    switch (type) {
      case OrderEventType.PAID:
        setOrder({
          ...order,
          paidAt: new Date(),
          events: [...(order?.events || []), newEvent],
        });
        break;
      case OrderEventType.CANCELED:
        setOrder({
          ...order,
          cancelledAt: new Date(),
          events: [...(order?.events || []), newEvent],
        });
        break;
      case OrderEventType.DELIVERED:
        setOrder({
          ...order,
          deliveredAt: new Date(),
          events: [...(order?.events || []), newEvent],
        });
        break;
      default:
        setOrder({ ...order, events: [...(order?.events || []), newEvent] });
    }
  };

  const renderTimelineItem = (event: any, ix: number) => {
    return (
      <div key={`event-${ix}`} className="uk-flex uk-flex-middle">
        <div
          className="uk-card uk-card-small uk-card-default uk-card-body"
          style={{
            minWidth: "200px",
            borderTop: "3px solid #1e87f0",
            padding: "8px 12px",
            background: "#fafafa",
          }}
        >
          <div
            className={`uk-text-bold uk-text-${handleOrderEventColor(event.type)} uk-text-small`}
          >
            {event.type}
          </div>
          <div className="uk-text-meta uk-text-xsmall uk-text-nowrap">
            {displayDateTime(event.eventAt)}
          </div>
          <div className="uk-text-meta uk-text-xsmall uk-text-bold">
            {event.user?.name}
          </div>
          {event.note && (
            <div className="uk-text-muted uk-text-italic uk-margin-small-top uk-text-xsmall">
              {event.note}
            </div>
          )}
        </div>
        {ix < (order?.events?.length || 0) - 1 && (
          <div
            className="uk-text-muted uk-text-xlarge"
            style={{ color: "#1e87f0" }}
          >
            →
          </div>
        )}
      </div>
    );
  };

  const handleOrderEventColor = (type: string): string => {
    const colorMap: { [key: string]: string } = {
      [OrderEventType.PAID.toLowerCase()]: "success",
      [OrderEventType.CANCELED.toLowerCase()]: "danger",
      [OrderEventType.IN_PRODUCTION.toLowerCase()]: "warning",
    };

    return colorMap[type?.toLowerCase()] || "primary";
  };

  if (!loading && !order) {
    return (
      <div className="uk-card uk-card-default uk-card-body">
        Pedido não encontrado.
      </div>
    );
  }

  return (
    <div id="page-order-edit">
      <div className="uk-card uk-card-default uk-card-small uk-card-body uk-position-relative">
        {loading && (
          <div
            className="uk-position-cover uk-overlay-primary uk-flex uk-flex-center uk-flex-middle"
            style={{ zIndex: 20 }}
          >
            <div className="uk-spinner" style={{ width: 48, height: 48 }} />
          </div>
        )}
        <div className="uk-grid-small" uk-grid="true">
          <div className="uk-width-1-1@s uk-flex uk-flex-between uk-flex-middle">
            <h3 className="uk-card-title">
              Detalhes do Pedido • Nº {order?.keyOrder}
            </h3>
            {!loading && (
              <div>
                {!order?.paidAt && !order?.cancelledAt && (
                  <button
                    onClick={() => setPaymentOpen(true)}
                    className="uk-button uk-button-secondary uk-button-small uk-margin-small-right"
                    disabled={loading}
                    style={{
                      background:
                        "linear-gradient(135deg, #7aea66ff 0%, #4ba278ff 100%)",
                      border: "none",
                      color: "white",
                      transition: "transform 0.2s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.transform = "scale(1.05)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.transform = "scale(1)")
                    }
                  >
                    Registrar Pagamento
                  </button>
                )}
                {!order?.cancelledAt && !order?.deliveredAt && (
                  <button
                    onClick={() =>
                      setDialog({ isOpen: true, action: "cancel" })
                    }
                    className="uk-button uk-button-danger uk-button-small uk-margin-small-right"
                    disabled={loading || order?.deliveredAt}
                    // style={{ background: 'linear-gradient(135deg, #ea6666ff 0%, #a24e4bff 100%)', border: 'none', color: 'white', transition: 'transform 0.2s' }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.transform = "scale(1.05)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.transform = "scale(1)")
                    }
                  >
                    Cancelar
                  </button>
                )}
                {order?.paidAt &&
                  !order?.cancelledAt &&
                  !order?.deliveredAt && (
                    <button
                      onClick={() =>
                        setDialog({ isOpen: true, action: "delivered" })
                      }
                      className="uk-button uk-button-danger uk-button-small uk-margin-small-right"
                      disabled={loading}
                      style={{
                        background:
                          "linear-gradient(135deg, rgb(66, 198, 231) 0%, rgb(28, 137, 161) 100%)",
                        border: "none",
                        color: "white",
                        transition: "transform 0.2s",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.transform = "scale(1.05)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.transform = "scale(1)")
                      }
                    >
                      Entregue
                    </button>
                  )}
                <button
                  onClick={handleResendEmail}
                  className="uk-button uk-button-primary uk-button-small uk-margin-small-right"
                  disabled={resending || loading}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.transform = "scale(1.05)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.transform = "scale(1)")
                  }
                >
                  {resending ? "Enviando..." : "Reenviar E-mail"}
                </button>
                {/* <Link to={`/pedidos/${params.type}`} className="uk-button uk-button-default uk-button-small">Voltar</Link> */}
                <button
                  className="uk-button uk-button-default uk-button-small"
                  onClick={() => window.close()}
                >
                  Voltar
                </button>
              </div>
            )}
          </div>
        </div>

        {order?.paidAt && (
          <span className="uk-badge uk-badge-success uk-margin-small-right">
            Pago em {displayDateTime(order.paidAt)}
          </span>
        )}
        {order?.cancelledAt && (
          <span className="uk-badge uk-badge-danger uk-margin-small-right">
            Cancelado em {displayDateTime(order.cancelledAt)}
          </span>
        )}

        <div className="uk-grid-small uk-margin-top" uk-grid="true">
          <div className="uk-width-1-2@s">
            <label className="uk-form-label uk-text-light">Nome</label>
            <div className="uk-form-controls uk-margin-small-top uk-text-light uk-text-bold uk-flex uk-flex-column">
              <label className="uk-text-nowrap">{order?.name}</label>
              <label className="uk-text-nowrap uk-text-small uk-text-muted">
                {authenticationService.isAdmin()
                  ? order && cpfMask(order?.document)
                  : `${order?.document?.substring(0, 3)}.***.***-${order?.document?.substring(order.document.length - 2)}`}
              </label>
            </div>
          </div>
          <div className="uk-width-1-3@s">
            <label className="uk-form-label uk-text-light">E-mail</label>
            <div className="uk-form-controls uk-margin-small-top uk-text-light uk-text-bold">
              {order?.email?.toLowerCase()}
            </div>
          </div>
          <div className="uk-width-1-6@s">
            <label className="uk-form-label uk-text-small">Feito Em</label>
            <div className="uk-form-controls uk-margin-small-top uk-text-meta uk-text-italic">
              {order?.createdDate ? displayDateTime(order.createdDate) : ""}
            </div>
          </div>
          <div className="uk-width-1-2@s">
            <label className="uk-form-label uk-text-light">Congregação</label>
            <div className="uk-form-controls uk-margin-small-top uk-text-light uk-text-bold">
              {order?.congregation?.description}
            </div>
          </div>
          <div className="uk-width-1-2@s">
            <label className="uk-form-label uk-text-light">Telefone</label>
            <div className="uk-flex uk-flex-middle uk-margin-small-top">
              <div className="uk-form-controls uk-text-light uk-text-bold">
                {order?.phone}
              </div>
              <FontAwesomeIcon
                icon={faWhatsapp as IconProp}
                size={"1x"}
                color="#19773b"
                className="uk-margin-small-left"
                style={{ cursor: "pointer" }}
                onClick={() =>
                  window.open(
                    `https://wa.me/55${order?.phone?.replace(/\D/g, "")}`,
                    "_blank",
                  )
                }
              />
            </div>
          </div>
        </div>

        <hr />

        <h4>Itens</h4>
        {!order?.items || order.items.length === 0 ? (
          <div>Nenhum item encontrado</div>
        ) : (
          <table className="uk-table uk-table-divider uk-table-small">
            <thead>
              <tr>
                <th>Produto</th>
                <th>Quantidade</th>
                <th>Preço</th>
                <th>Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((it: any, ix: number) => (
                <tr key={`item-${ix}`}>
                  <td>{it.description}</td>
                  <td>{it.qty}</td>
                  <td>{currencyMask(it.price)}</td>
                  <td>{currencyMask(it.qty * it.price)}</td>
                </tr>
              ))}
              <tr>
                <td className="uk-text-right uk-text-light">QTDE. ITENS</td>
                <td className="uk-text-bold">
                  {order?.items?.reduce(
                    (acc: number, it: any) => acc + (it.qty || 0),
                    0,
                  )}
                </td>
                <td className="uk-text-right uk-text-light">TOTAL</td>
                <td className="uk-text-bold">
                  {currencyMask(
                    (order?.items ?? []).reduce(
                      (acc: number, it: any) =>
                        acc + (it.qty || 0) * (it.price || 0),
                      0,
                    ),
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        )}

        <hr />

        <h5 className="uk-text-bold uk-text-muted uk-text-center">Histórico</h5>
        {!order?.events || order.events.length === 0 ? (
          <div className="uk-text-muted uk-text-small">
            Nenhum evento encontrado
          </div>
        ) : (
          <div className="uk-overflow-auto uk-margin-xsmall-y">
            <div className="uk-flex uk-flex-middle uk-flex-center">
              {order.events.map((event: any, ix: number) =>
                renderTimelineItem(event, ix),
              )}
            </div>
          </div>
        )}
      </div>

      <OrderPayment
        open={isPaymentOpen}
        payment={
          {
            keyOrder: order?.keyOrder,
            idOrder: order?.id,
            paidValue: order?.amountDue ?? 0,
          } as IOrderPaymentDTO
        }
        afterHandleSubmit={(paidAt: Date) => {
          setPaymentOpen(false);
          handleNewEvent(OrderEventType.PAID);
          toast.success("Pagamento registrado com sucesso!", toastOptions);
        }}
        afterCloseModal={() => setPaymentOpen(false)}
      />

      <DialogConfirm
        open={dialog.isOpen}
        title={
          dialog.action === "cancel"
            ? "Confirmar Cancelamento do Pedido"
            : "Confirmar Entrega do Pedido"
        }
        text={
          dialog.action === "cancel"
            ? [
                "Deseja realmente cancelar este pedido? Esta ação não poderá ser desfeita.",
              ]
            : ["Deseja marcar este pedido como entregue?"]
        }
        confirmTextLabel={
          dialog.action === "cancel"
            ? "Digite a senha de confirmação"
            : undefined
        }
        handleConfirmation={(confirmationCode: string) =>
          handleDialogConfirmation(confirmationCode)
        }
        afterClose={() => setDialog({ isOpen: false, action: null })}
      />
    </div>
  );
}
